import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '../../shared/types';
import { authApi } from '../../shared/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[];

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: [],

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login(credentials);
          const { user } = response;

          // Extract permissions from user roles
          const permissions = user.roles.flatMap((role) => role.permissions);

          set({
            user,
            isAuthenticated: true,
            permissions,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } catch (error) {
          // Continue with logout even if API call fails
          console.error('Logout API call failed:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            permissions: [],
            isLoading: false,
          });
        }
      },

      checkAuth: async () => {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          set({
            user: null,
            isAuthenticated: false,
            permissions: [],
            isLoading: false,
          });
          return;
        }

        set({ isLoading: true });
        try {
          const user = await authApi.getCurrentUser();
          const permissions = user.roles.flatMap((role) => role.permissions);

          set({
            user,
            isAuthenticated: true,
            permissions,
            isLoading: false,
          });
        } catch (error) {
          // Token is invalid, clear auth state
          set({
            user: null,
            isAuthenticated: false,
            permissions: [],
            isLoading: false,
          });
          // Clear tokens from localStorage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      },

      refreshToken: async () => {
        try {
          await authApi.refreshToken();
          // After successful refresh, check auth to update user data
          await get().checkAuth();
        } catch (error) {
          // Refresh failed, logout user
          await get().logout();
          throw error;
        }
      },

      hasPermission: (permission: string) => {
        const { permissions, user } = get();

        // Superuser has all permissions
        if (user?.isSuperuser) {
          return true;
        }

        // Check exact permission match
        if (permissions.includes(permission)) {
          return true;
        }

        // Check wildcard permissions
        return permissions.some((p) => {
          if (p.endsWith('*')) {
            const prefix = p.slice(0, -1);
            return permission.startsWith(prefix);
          }
          return false;
        });
      },

      setUser: (user: User | null) => {
        const permissions = user
          ? user.roles.flatMap((role) => role.permissions)
          : [];
        set({
          user,
          isAuthenticated: !!user,
          permissions,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-store',
      // Only persist user data, not loading states
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
      }),
    }
  )
);
